/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable react/jsx-no-useless-fragment */

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import {
  Avatar,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { iconGlassChip } from 'components/Icon/IconSurface';
import { TFunction } from 'i18next';
import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { LogoutSession } from 'services/api/UserApi';
import { useAppSelector } from 'store/hooks';
import { LogoutUser } from 'store/store';
import { TProps_SideBar } from 'typings';
import { ROUTES } from 'utils/constants';

export const getRouteHeadingMapping = (
  t: TFunction<'translation', 'layout.topBar'>
) => {
  return [
    {
      route: ROUTES.DASHBOARD,
      heading: t('dashboard')
    },
    {
      route: ROUTES.BASE_PLANS_AUTO_TOP_UP,
      heading: 'plans'
    },
    {
      route: ROUTES.SUBSCRIPTION_LIST,
      heading: t('subscription')
    },
    {
      route: '/subscription',
      heading: t('subscription')
    },
    {
      route: ROUTES.BILLINGS,
      heading: t('billing')
    },
    {
      route: ROUTES.REVIEW_PURCHASE,
      heading: t('reviewPlan')
    },
    {
      route: ROUTES.FAQ,
      heading: t('faq')
    },
    {
      route: ROUTES.CAMERA_LIMIT_REACHED,
      heading: t('subscription')
    },
    {
      route: ROUTES.SUPPORT,
      heading: t('helpAndSupport')
    }
  ];
};

function TopBar(props: TProps_SideBar) {
  const { setOpenSideBar, openSideBar } = props;
  const location = useLocation();
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'layout.topBar'
  });

  const [heading, setHeading] = useState(t('userPortal'));

  const routeHeadingMapping = getRouteHeadingMapping(t);

  const getHeading = () =>
    routeHeadingMapping.find((el) => location.pathname.search(el.route) >= 0)
      ?.heading;

  const { userDetails } = useAppSelector((state) => state.user);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );

  useEffect(() => {
    const header = getHeading();
    console.log('language topbar', i18n.language);
    if (header) {
      setHeading(header);
    }
  }, [location.pathname]);

  const handleOpenUserMenu = (event: {
    currentTarget: React.SetStateAction<HTMLElement | null>;
  }) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const onLogOut = async () => {
    try {
      await LogoutSession();
      LogoutUser();
    } catch (err) {
      console.error(err);
    }
  };

  const avatarNode = (
    <Tooltip title={`${userDetails?.name.first} ${userDetails?.name.last}`}>
      <IconButton
        onClick={handleOpenUserMenu}
        sx={(theme) => ({
          p: 0,
          borderRadius: theme.radius.full,
          transition: 'transform 150ms ease, filter 150ms ease',
          '&:hover': { filter: 'brightness(1.06)' },
          '&:active': { transform: 'scale(0.96)' },
          // Was `.btn-no-focus` (a global `:focus { outline: none }` rule) —
          // dropped here specifically so keyboard focus gets a real ring
          // instead of none at all; `:focus-visible` keeps a plain mouse
          // click from showing it.
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2
          }
        })}
      >
        {/* Product-tinted glass — reads as the active brand colour, not a
            flat grey circle, while matching the app's glass system. Same
            "active glass" recipe the selected plan card and toggle use
            (see IconSurface's `glassTinted`) — the initial stays a plain
            explicit white rather than the surface's own `text.primary`,
            since it has to read against the tinted fill in both modes, not
            the page. */}
        <Avatar
          sx={(theme) => ({
            position: 'relative',
            width: 40,
            height: 40,
            ...iconGlassChip(theme, 'glassTinted'),
            color: '#FFFFFF',
            fontWeight: 600
          })}
        >
          {userDetails?.name.first?.[0]?.toUpperCase()}
        </Avatar>
      </IconButton>
    </Tooltip>
  );

  const isAddCameraInfo =
    location.pathname === ROUTES.ADD_CAMERA_INFO ||
    location.pathname === ROUTES.ADD_HUB_INFO;

  // Shared hover/press/focus polish for both states of the toggle below —
  // one definition so the two don't drift out of sync.
  const menuButtonInteraction = (theme: import('@mui/material').Theme) => ({
    transition: 'transform 150ms ease, filter 150ms ease',
    '&:hover': { filter: 'brightness(1.06)' },
    '&:active': { transform: 'scale(0.94)' },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2
    }
  });

  // Sidebar toggle — lives on the LEFT on mobile. Hidden on the add-camera
  // flow; on desktop the rail is permanent so there's no toggle at all.
  const renderMenuButton = () => {
    if (!isMobile || isAddCameraInfo) return null;
    if (!openSideBar) {
      return (
        <IconButton
          onClick={() => setOpenSideBar(true)}
          aria-label="open menu"
          sx={(theme) => ({
            position: 'relative',
            width: 42,
            height: 42,
            borderRadius: theme.radius.full,
            ...iconGlassChip(theme, 'glass'),
            // After the spread: glassSurface's own `color` is already
            // `text.primary`, so this is a no-op today, but explicit rather
            // than relying on that happening to match — the line icon has
            // to stay dark/high-contrast regardless of what the surface
            // recipe's default is.
            color: theme.palette.text.primary,
            ...menuButtonInteraction(theme)
          })}
        >
          <MenuRoundedIcon />
        </IconButton>
      );
    }
    return (
      <IconButton
        onClick={() => setOpenSideBar(false)}
        aria-label="close menu"
        sx={(theme) => ({
          position: 'relative',
          width: 42,
          height: 42,
          borderRadius: theme.radius.full,
          ...iconGlassChip(theme, 'glassTinted'),
          // After the spread: tintedGlassSurface's own `color` is
          // `text.primary`, which would flatten this "active" icon to the
          // same neutral colour as the bare hamburger state above — this
          // keeps it the product colour, matching what it looked like
          // before this restyle.
          color: theme.palette.primary.main,
          ...menuButtonInteraction(theme)
        })}
      >
        <CloseRoundedIcon fontSize="small" />
      </IconButton>
    );
  };

  const menuButtonNode = renderMenuButton();

  if (!openSideBar || !isMobile) {
    return (
      <>
        <Stack
          sx={{
            flexDirection: 'row',
            padding: '16px 20px',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            backgroundColor: (theme) => theme.palette.background.default,
            top: 0,
            width: '100%',
            zIndex: 1299,
            minHeight: (theme) => theme.spacing(10)
          }}
        >
          <Menu
            sx={{
              mt: '45px'
            }}
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
            PaperProps={{
              sx: {
                borderRadius: (theme) => theme.radius.md,
                border: (theme) => `1px solid ${theme.palette.border.divider}`,
                boxShadow: (theme) => theme.palette.shadows.sheet,
                minWidth: 160
              }
            }}
          >
            <MenuItem onClick={onLogOut}>
              <Typography textAlign="center">{t('logout')}</Typography>
            </MenuItem>
          </Menu>

          {isMobile ? (
            <>
              {/* left slot — keeps the heading centred even with no toggle */}
              {menuButtonNode ?? <div style={{ width: 40, flexShrink: 0 }} />}
              <Typography
                variant="titleMedium"
                sx={{ flex: 1, textAlign: 'center' }}
              >
                {heading}
              </Typography>
              {avatarNode}
            </>
          ) : (
            <>
              <Typography variant="titleMedium">{heading}</Typography>
              {avatarNode}
            </>
          )}
        </Stack>
        <Divider />
      </>
    );
  }

  return null;
}

export default TopBar;
